import classNames from 'classnames';
import { useEffect, useMemo, useRef, useState } from 'react';
import Image from '@/components/atoms/image/image';
import DragScroll from '@/components/utilities/drag-scroll/drag-scroll';
import Modal from '@/components/utilities/modal/modal';
import Button from '../button/button';
import styles from './image-with-zoom.module.scss';

export interface ImageWithZoomProps {
  image: Content.Properties.Image;
  maximumZoom?: number;
}

const ImageWithZoom = ({ image, maximumZoom = 5 }: ImageWithZoomProps) => {
  const imageRef = useRef<HTMLDivElement>(null);
  const dialogImageRef = useRef<HTMLDivElement>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [zoom, setZoom] = useState(1);
  const [scrollProgress, setScrollProgress] = useState({ left: 0.5, top: 0.5 });
  const [random, setRandom] = useState(0);

  const activeRef = useMemo(() => {
    return isDialogOpen ? dialogImageRef : imageRef;
  }, [isDialogOpen]);

  const saveScrollPosition = () => {
    if (!activeRef.current) return;

    const {
      scrollTop,
      scrollHeight,
      scrollLeft,
      scrollWidth,
      clientWidth,
      clientHeight,
    } = activeRef.current;

    const scrollProgressTop = scrollTop / (scrollHeight - clientHeight);
    const scrollProgressLeft = scrollLeft / (scrollWidth - clientWidth);

    setScrollProgress(previousState => ({
      left: isNaN(scrollProgressLeft) ? previousState.left : scrollProgressLeft,
      top: isNaN(scrollProgressTop) ? previousState.top : scrollProgressTop,
    }));
  };

  const restoreScrollPosition = () => {
    if (!activeRef.current) return;

    const { scrollHeight, scrollWidth, clientHeight, clientWidth } =
      activeRef.current;

    const scrollY = (scrollHeight - clientHeight) * scrollProgress.top;
    const scrollX = (scrollWidth - clientWidth) * scrollProgress.left;

    activeRef.current.scrollTo(scrollX, scrollY);
  };

  const handleZoomChange = (zoom: number) => {
    if (!activeRef.current) return;

    saveScrollPosition();

    let newZoom = zoom;

    if (newZoom < 1) newZoom = 1;

    if (newZoom > maximumZoom) newZoom = maximumZoom;

    setZoom(newZoom);
  };

  const handleToggleDialog = () => {
    saveScrollPosition();
    setIsDialogOpen(previousState => !previousState);
  };

  useEffect(() => {
    setScrollProgress({ left: 0.5, top: 0.5 });
    setZoom(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [image]);

  useEffect(() => {
    setRandom(Math.random());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [zoom, activeRef, image]);

  useEffect(() => {
    restoreScrollPosition();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [random]);

  const imageRender = (
    <Image
      alt={image.altText}
      className={classNames(styles.image, {
        [styles.isSquare]:
          parseInt(`${image.dimensions?.width}`) ===
          parseInt(`${image.dimensions?.height}`),
        [styles.isTaller]:
          parseInt(`${image.dimensions?.height}`) >
          parseInt(`${image.dimensions?.width}`),
        [styles.isWider]:
          parseInt(`${image.dimensions?.width}`) >
          parseInt(`${image.dimensions?.height}`),
      })}
      src={image.url}
      srcSet={{
        desktop: {
          width: 1344 * zoom,
        },
        laptop: {
          width: 960 * zoom,
        },
        tablet: {
          width: 720 * zoom,
        },
      }}
      style={{
        aspectRatio: `${image.dimensions?.width} / ${image.dimensions?.height}`,
        transform: `scale(${zoom})`,
      }}
      unoptimized
      width={343 * zoom}
    />
  );

  const controlsRender = (
    <div className={styles.controls}>
      <Button
        className={styles.button}
        disabled={zoom >= maximumZoom}
        icon="Plus"
        iconSize={24}
        onClick={() => handleZoomChange(zoom + 1)}
      />

      <Button
        className={styles.button}
        disabled={zoom <= 1}
        icon="Minus"
        iconSize={24}
        onClick={() => handleZoomChange(zoom - 1)}
      />

      <Button
        className={styles.button}
        icon={isDialogOpen ? 'Shrink' : 'Expand'}
        iconSize={24}
        onClick={handleToggleDialog}
      />
    </div>
  );

  return (
    <div
      className={classNames(styles.component, {
        [styles.hasOpenDialog]: isDialogOpen,
      })}
    >
      <Modal isOpen={isDialogOpen} setIsOpen={setIsDialogOpen}>
        <DragScroll ref={dialogImageRef} className={styles.dragArea}>
          {imageRender}
        </DragScroll>

        {controlsRender}
      </Modal>

      <DragScroll ref={imageRef} className={styles.dragArea}>
        {imageRender}
      </DragScroll>

      {controlsRender}
    </div>
  );
};

ImageWithZoom.displayName = 'ImageWithZoom';

export default ImageWithZoom;
